import React from 'react';
import PropTypes from 'prop-types';
import {Popup} from 'semantic-ui-react';
import {isNil as _isNil} from 'lodash';

import {IconWithTooltip} from '@appComponents/IconCollection';
import {SRT_ORIGIN_ENTRY_POINT} from '@constants/resources';
import {subtractMinutesFromDate, addMinutesToDate} from '@utils/momentWrapper';
import {OutputTunnel} from '@modules/events/components/OutputTunnel';
import {link} from '@modules/events/utils/parseEventContentInputs';
import {buildArgosDashboardJobDefinitionUrl} from '@modules/events/utils/argosDashboardUrl';
import JobStatusLabel from '@modules/events/components/ArgosSubpartsContent/JobStatusLabel';
import StreamStatusLabel from '@modules/events/components/ArgosSubpartsContent/StreamStatusLabel';

export const renderJobDefinition = (data) => (
    <span>
        <Popup
            content={data.argos_job_definition.id_string}
            trigger={<span>{data.argos_job_definition?.name}</span>}
            position='top center'
            hoverable
        />
        <IconWithTooltip
            iconProps={{
                className: 'job-definition__icon',
                onClick: () => window.open(
                    buildArgosDashboardJobDefinitionUrl(data.argos_job_definition.id_string),
                    '_blank'
                ),
            }}
            name='external link'
            tooltip='Open the Argos job definition in the Argos dashboard'
        />
    </span>
);

export const renderArgosJobTime = ({
    type,
    data,
    eventDatetime,
}) => {
    const isStart = 'start' === type;
    const jobTime = isStart ? data?.start_datetime : data?.end_datetime;
    const margin = isStart ? data?.start_margin : data?.end_margin;
    const hasEventDatetime = !_isNil(eventDatetime);

    if (jobTime) {
        if (!_isNil(margin)) {
            return (
                <Popup
                    content={isStart ? `${margin} minutes before the event start time` : `${margin} minutes after the event end time`}
                    trigger={<span>{`${jobTime} (${isStart ? '-' : '+'}${margin} min)`}</span>}
                    position='top center'
                    hoverable
                />
            );
        }

        return jobTime;
    }

    if (!_isNil(margin)) {
        if (hasEventDatetime) {
            const calcTime = isStart
                ? subtractMinutesFromDate(eventDatetime, margin)
                : addMinutesToDate(eventDatetime, margin);

            return (
                <Popup
                    content={isStart
                        ? `${margin} minutes before the event start time`
                        : `${margin} minutes after the event end time`}
                    trigger={<span>{`${calcTime} (${isStart ? '-' : '+'}${margin} min)`}</span>}
                    position='top center'
                    hoverable
                />
            );
        }

        if (!isStart) {
            return (
                <Popup
                    content={`${margin} minutes after the end of the match`}
                    trigger={<span>{`+${margin} min`}</span>}
                    position='top center'
                    hoverable
                />
            );
        }
    }

    return 'Manual';
};

export const ArgosSubpartsContent = ({data, eventStartDatetime, eventEndDatetime, eventId, jobId}) => {
    const details = [
        [
            {
                name: 'Job definition',
                value: renderJobDefinition(data),
            },
            {
                name: 'Region',
                value: (<Popup
                    content={data.argos_job_region.id_string}
                    trigger={<span>{data.argos_job_region?.name}</span>}
                    position='top center'
                    hoverable
                />),
            },
        ],
        [
            {
                name: 'Entry point',
                value: (<Popup
                    content={`URL: ${data.srt_origin_entry_point.url}`}
                    trigger={
                        <span>{
                            link(data.srt_origin_entry_point, 'srtOriginEntryPoints.edit',
                                SRT_ORIGIN_ENTRY_POINT, {newWindow: true})}
                        </span>
                    }
                    position='top center'
                    hoverable
                />),
            },
            {
                name: 'Output tunnel',
                value: <OutputTunnel tunnel={data.third_party_client_tunnel} />,
            },
        ],
        [
            {
                name: 'Start time',
                value: renderArgosJobTime({type: 'start', data, eventDatetime: eventStartDatetime}),
            },
            {
                name: 'End time',
                value: renderArgosJobTime({type: 'end', data, eventDatetime: eventEndDatetime}),
            },
        ],
        [
            {
                name: 'Job status',
                value: <JobStatusLabel jobUuid={data.job_uuid} liveJob={data.live_job} message={data.message} state={data.state} eventId={eventId} jobId={jobId} />,
            },
            {
                name: 'Stream status',
                value: <StreamStatusLabel liveJob={data.live_job} eventId={eventId} jobId={jobId} />,
            },
        ],
    ];

    const rows = details[0].map((_, rowIdx) => details.map(col => col[rowIdx]));

    return (
        <div className='argos-subparts-content'>
            {rows.map((row) => (
                <div className='argos-subparts-row' key={row.map(col => col.name).join('-')}>
                    {row.map((column, idx) => (
                        <div className='job-info-row' key={`${column.name}-${idx}`}>
                            <span className='job-info-label'>{column.name}</span>
                            <span className='job-info-value'>{column.value}</span>
                        </div>
                    ))}
                </div>
            ))}
        </div>
    );
};

ArgosSubpartsContent.propTypes = {
    data: PropTypes.shape({
        argos_job_definition: PropTypes.shape({
            name: PropTypes.string,
            id_string: PropTypes.string,
        }),
        argos_job_region: PropTypes.shape({
            name: PropTypes.string,
            id_string: PropTypes.string,
        }),
        srt_origin_entry_point: PropTypes.object,
        third_party_client_tunnel: PropTypes.object,
        start_datetime: PropTypes.string,
        end_datetime: PropTypes.string,
        start_margin: PropTypes.number,
        job_uuid: PropTypes.string,
        message: PropTypes.string,
        state: PropTypes.string,
        live_job: PropTypes.shape({
            status: PropTypes.shape({
                state: PropTypes.string,
                code: PropTypes.number,
                description: PropTypes.string,
            }),
            stream: PropTypes.shape({
                status: PropTypes.string,
                events: PropTypes.array,
            }),
            job_history: PropTypes.array,
        }),
    }),
    eventStartDatetime: PropTypes.string,
    eventEndDatetime: PropTypes.string,
    eventId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    jobId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};
